import { useCallback, useEffect, useMemo, useState } from 'react'
import { CalendarDays, CheckCircle2, ExternalLink, Merge, Search } from 'lucide-react'
import { cmsApi } from '../lib/api'
import { cardImagePosition, statusLabel } from '../lib/format'
import type { DuplicateGroup, EventSummary } from '../types'
import { LoadingState } from './LoadingState'
import { SheetSyncNotice } from './MergeDialog'
import { EventSummaryLine } from './SimilarEvents'

type Props = {
  canEdit: boolean
  catalogEditing: boolean
  onOpenEvent: (id: string) => void
  onSaved: (message: string) => void
  onError: (message: string) => void
}

// The one a group keeps by default: the most sessions, then favorites, then
// the latest dates.
const likeliestKeeper = (events: EventSummary[]) => [...events].sort((left, right) =>
  right.sessions - left.sessions || right.saves - left.saves || String(right.last_date ?? '').localeCompare(String(left.last_date ?? '')),
)[0]?.id

// Events with the same words, for cleaning the catalog (docs/cms-hub.md):
// each group keeps one, and the others join it.
export function DuplicatesPage({ canEdit, catalogEditing, onOpenEvent, onSaved, onError }: Props) {
  const [groups, setGroups] = useState<DuplicateGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [keepers, setKeepers] = useState<Record<string, string>>({})
  const [merging, setMerging] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setGroups(await cmsApi.duplicateGroups())
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Não foi possível carregar os duplicados.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const shown = useMemo(() => {
    const words = search.trim().toLocaleLowerCase('pt-BR')
    return words ? groups.filter((group) => group.events.some((event) => event.title.toLocaleLowerCase('pt-BR').includes(words))) : groups
  }, [groups, search])

  const merge = async (group: DuplicateGroup) => {
    const keepId = keepers[group.words] ?? likeliestKeeper(group.events)
    const keep = group.events.find((event) => event.id === keepId)
    const others = group.events.filter((event) => event.id !== keepId)
    if (!keep || !others.length) return
    if (!window.confirm(`Juntar ${others.length} ${others.length === 1 ? 'evento' : 'eventos'} em “${keep.title}”? ${others.length === 1 ? 'O outro deixa' : 'Os outros deixam'} de existir.`)) return
    setMerging(group.words)
    try {
      for (const other of others) await cmsApi.mergeEvents(keep.id, other.id)
      onSaved(`${others.length + 1} eventos juntados em “${keep.title}”.`)
      await load()
    } catch (mergeError) {
      onError(mergeError instanceof Error ? mergeError.message : 'Não foi possível juntar os eventos.')
      await load()
    } finally {
      setMerging('')
    }
  }

  return (
    <div className="page-stack">
      <section className="page-heading">
        <h1>Duplicados</h1>
        <p>Eventos com o mesmo nome. Escolha o que fica: os outros entram nele, com locais, horários, favoritos e endereço.</p>
      </section>

      <SheetSyncNotice catalogEditing={catalogEditing} />

      <section className="panel panel--flush">
        <div className="filter-bar">
          <label className="search-field">
            <Search size={18} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por título" />
          </label>
        </div>

        {loading ? <LoadingState label="Procurando duplicados…" /> : error ? (
          <div className="error-state"><p>{error}</p><button className="button button--secondary" onClick={() => void load()}>Tentar novamente</button></div>
        ) : shown.length ? (
          <div className="duplicate-list">
            {shown.map((group) => {
              const keepId = keepers[group.words] ?? likeliestKeeper(group.events)
              const keep = group.events.find((event) => event.id === keepId)
              return (
                <article className="duplicate-group" key={group.words}>
                  <div className="duplicate-group__heading">
                    <div><strong>{keep?.title ?? group.events[0].title}</strong><span>{group.events.length} eventos com o mesmo nome</span></div>
                    {canEdit && (
                      <button className="button button--primary" disabled={Boolean(merging)} onClick={() => void merge(group)}>
                        <Merge size={16} /> {merging === group.words ? 'Juntando…' : `Juntar em um só`}
                      </button>
                    )}
                  </div>
                  <div className="duplicate-group__events" role="radiogroup" aria-label={`Qual fica: ${group.events[0].title}`}>
                    {group.events.map((event) => (
                      <label key={event.id} className={`merge-option ${event.id === keepId ? 'merge-option--selected' : ''}`}>
                        <input type="radio" name={group.words} aria-label={`Fica: ${event.title}`} disabled={!canEdit} checked={event.id === keepId}
                          onChange={() => setKeepers((current) => ({ ...current, [group.words]: event.id }))} />
                        <span className="compact-event__media">{event.image_url ? <img src={event.image_url} alt="" style={{ objectPosition: cardImagePosition(event) }} /> : <CalendarDays size={18} />}</span>
                        <span className="similar-event__main"><strong>{event.title}</strong><EventSummaryLine event={event} /></span>
                        <span className="duplicate-group__meta">
                          {event.id === keepId && <span className="keeper-pill"><CheckCircle2 size={13} /> Fica</span>}
                          <span className={`status-pill status-pill--${event.status}`}>{statusLabel(event.status)}</span>
                          <button type="button" className="icon-button" aria-label={`Abrir ${event.title}`} onClick={(click) => { click.preventDefault(); onOpenEvent(event.id) }}><ExternalLink size={16} /></button>
                        </span>
                      </label>
                    ))}
                  </div>
                </article>
              )
            })}
          </div>
        ) : (
          <div className="empty-state"><CheckCircle2 size={30} /><h3>Nenhum duplicado</h3><p>Nenhum evento com o mesmo nome de outro.</p></div>
        )}
        {!loading && !error && <div className="pagination"><span>{groups.length.toLocaleString('pt-BR')} {groups.length === 1 ? 'grupo' : 'grupos'}</span></div>}
      </section>
    </div>
  )
}
